import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Barcode, Printer, Sparkles, TriangleAlert } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { Product } from '@/lib/types'
import { formatQuantity } from '@/lib/money'
import { renderLabelsHtml, type ProductLabel } from '@/lib/printing/labelHtml'
import { printHtml } from '@/lib/printing/adapters'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/primitives'
import { t } from '@/i18n'

/** One product that came in on a delivery, and how many of it. */
export interface DeliveredItem {
  productId: number
  quantity: number
}

/**
 * Labels for everything that just came in.
 *
 * Booking stock in and labelling it are one job done at one bench, so this sits inside the
 * delivery window rather than being somewhere else to navigate to. Copies default to the quantity
 * received, because that is how many items are standing on the floor waiting for a sticker; a
 * clerk who wants fewer says so, but nobody should have to type the number they just typed.
 */
export function DeliveryLabels({
  items,
  products,
  title,
  description,
}: {
  items: DeliveredItem[]
  products: Product[]
  title: string
  description?: string
}) {
  const queryClient = useQueryClient()

  /**
   * Only what the clerk has overridden, by position on the delivery.
   *
   * Keyed on the line rather than the product, because one product can legitimately arrive twice
   * on one delivery — two batches, two expiry dates — and keying on the product made those two
   * rows a single box that set both.
   *
   * The default is the quantity received, derived on every render rather than copied into state
   * by an effect. Seeding state from a prop that is rebuilt on each render — which the reprint
   * view does, mapping a delivery's lines inline — set state, forced a render, produced a new
   * array, and ran the effect again for as long as the page was open.
   */
  const [overrides, setOverrides] = useState<Record<number, string>>({})
  const [error, setError] = useState<string | null>(null)

  const byId = new Map(products.map((product) => [product.id, product]))

  // A weighed item does not come in units, so 2.5 kg is not two and a half labels; anything
  // fractional rounds to a whole sticker unless the clerk says otherwise.
  const defaultCopies = (quantity: number) =>
    String(Math.max(1, Math.min(500, Math.round(quantity) || 1)))

  const copiesFor = (item: DeliveredItem, index: number) =>
    overrides[index] ?? defaultCopies(item.quantity)

  const mint = useMutation({
    mutationFn: (productId: number) =>
      apiClient.post<{ code: string }>(`/api/products/${productId}/barcodes`, {
        code: null,
        packQuantity: 1,
      }),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not mint a code.',
      ),
  })

  const print = useMutation({
    mutationFn: async (only: number | null) => {
      // Prices and the store name come from the server, so a label can never promise something
      // the till would not charge.
      const requests = items
        .map((item, index) => ({ item, index }))
        .filter(({ index }) => only === null || index === only)
        .filter(({ item }) => byId.get(item.productId)?.primaryBarcode)
        .map(({ item, index }) => ({
          productId: item.productId,
          copies: Number(copiesFor(item, index) || 1),
          code: null,
        }))

      if (requests.length === 0) {
        throw new ApiError(400, 'Nothing to print', 'None of these has a barcode yet.')
      }

      const labels = await apiClient.post<ProductLabel[]>('/api/products/labels', requests)
      await printHtml(renderLabelsHtml(labels))
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not print.'),
  })

  const unlabelled = items.filter((item) => !byId.get(item.productId)?.primaryBarcode).length

  return (
    <section className="surface-raised rounded-[var(--radius-card)] border p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {description && (
            <p className="text-xs text-[var(--text-secondary)]">{description}</p>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => {
            setError(null)
            print.mutate(null)
          }}
          disabled={print.isPending || items.length === unlabelled}
        >
          <Printer className="h-4 w-4" />
          {print.isPending ? 'Printing…' : 'Print all'}
        </Button>
      </div>

      <ul className="flex flex-col gap-2">
        {items.map((item, index) => {
          const product = byId.get(item.productId)
          if (!product) return null

          return (
            <li
              key={index}
              className="surface-sunken flex flex-wrap items-center gap-3 rounded-lg p-3"
            >
              <div className="min-w-40 flex-1">
                <p className="truncate text-sm font-medium">{product.name}</p>
                <p className="tabular truncate text-xs text-[var(--text-secondary)]">
                  {formatQuantity(item.quantity)} received ·{' '}
                  {product.primaryBarcode ?? 'no barcode yet'}
                </p>
              </div>

              {product.primaryBarcode ? (
                <>
                  <Input
                    label={t('Copies')}
                    type="number"
                    min="1"
                    max="500"
                    value={copiesFor(item, index)}
                    onChange={(event) =>
                      setOverrides((current) => ({ ...current, [index]: event.target.value }))
                    }
                    wrapperClassName="w-24"
                    className="tabular"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setError(null)
                      print.mutate(index)
                    }}
                    disabled={print.isPending}
                  >
                    <Printer className="h-4 w-4" />
                    {t('Print')}
                  </Button>
                </>
              ) : (
                <>
                  <Badge tone="warn">{t('Needs a code')}</Badge>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => mint.mutate(item.productId)}
                    disabled={mint.isPending}
                  >
                    <Sparkles className="h-4 w-4" />
                    {t('Generate')}
                  </Button>
                </>
              )}
            </li>
          )
        })}
      </ul>

      {items.length === 0 && (
        <p className="flex items-center gap-2 py-4 text-sm text-[var(--text-secondary)]">
          <Barcode className="h-4 w-4" />
          {t('Nothing on this delivery.')}
        </p>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-danger-500">
          <TriangleAlert className="mr-1 inline h-4 w-4" />
          {error}
        </p>
      )}
    </section>
  )
}
